import React, { createContext, useContext, useMemo, useCallback, ReactNode, useState, useEffect, useRef } from 'react';
import type { ServiceRolesMap, ServiceRole } from '../types';
import { PRIVILEGED_ROLES } from '../constants';
import api from '../lib/api';
import { tokenManager } from '../infrastructure/security/tokenManager';
import {
  getActiveSocietyId,
  setActiveSocietyId,
  ACTIVE_SOCIETY_CHANGE_EVENT,
} from '../shared/utils/activeSociety';

export interface SocietyContextItem {
  societyId: number;
  societyName: string;
  roleTypeCode: string;
  roleTypeId: number;
  permissions: string[];
}

interface AuthContextType {
  /** True when user is actively authenticated */
  isAuthenticated: boolean;
  /** Active effective role code for current society context */
  role: string;
  /** Active permission codes granted to this user in current society context */
  permissions: string[];
  /** All permissions across all roles */
  allPermissions?: string[];
  /** Active society ID from society context */
  activeSocietyId?: number | null;
  /** Available societies the user belongs to */
  availableSocieties?: SocietyContextItem[];
  /** Function to switch active society context */
  switchSociety?: (societyId: number | null) => void;
  /** User's numeric ID */
  userId: number | undefined;
  /** Society member ID of the logged-in user (different from userId) */
  memberId: number | undefined;
  /** Active society ID from login context */
  societyId: number | undefined;
  /** Profile ID of the logged-in user */
  profileId: number | undefined;
  /** Member type code (e.g. 'PRIMARY', 'PRIMARY_MEMBER', 'SECONDARY', 'SECONDARY_MEMBER') */
  memberType?: string;
  /** User's first name (from login response) */
  firstName: string;
  /** User's last name (from login response) */
  lastName?: string;
  /** User's email (from login response) */
  email?: string;
  /** True if the user's email was verified via OTP */
  emailVerified: boolean;
  /** True if the user holds any privileged (edit-capable) role */
  canEdit: boolean;
  /** True if the user's only role is a read-only member */
  isReadOnly: boolean;
  /** Shorthand for role === 'MEMBER' */
  isMember: boolean;
  /** Shorthand for role === 'SOCIETY_GUARD' */
  isGuard: boolean;
  /** Stable callback — checks a single permission code */
  hasPermission: (permission: string) => boolean;
  /** Service-scoped roles map (serviceCode → ServiceRole) */
  serviceRoles: ServiceRolesMap;
  /** Get the service-specific role for a given service code */
  getServiceRole: (serviceCode: string) => ServiceRole | undefined;
  /** Check if user has edit privileges for a specific service */
  canEditService: (serviceCode: string) => boolean;
  /** Check if user has a specific permission within a service */
  hasServicePermission: (serviceCode: string, permission: string) => boolean;
  /** Triggers a silent refresh of the user session details from the server */
  refreshSession: () => Promise<void>;
  /** Called immediately on login to persist and synchronize auth state across the app */
  login: (data: any) => void;
  /** Clears local storage and resets auth state */
  logout: () => void;
  /** Marks email as verified in context + localStorage (called after inline OTP in subscription screen) */
  markEmailVerified: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readLocalStorage = (key: string, fallback = ''): string => {
  try { return localStorage.getItem(key) ?? fallback; }
  catch { return fallback; }
};

const readLocalStorageJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
};

interface AuthDataState {
  role: string;
  permissions: string[];
  userId: number | undefined;
  memberId: number | undefined;
  societyId: number | undefined;
  profileId: number | undefined;
  memberType?: string;
  firstName: string;
  lastName?: string;
  email?: string;
  emailVerified: boolean;
  serviceRoles: ServiceRolesMap;
}

const getInitialAuthData = (): AuthDataState => {
  const isAuth = readLocalStorage('xservices_authenticated') === 'true';
  if (!isAuth) {
    return {
      role: '',
      permissions: [],
      userId: undefined,
      memberId: undefined,
      societyId: undefined,
      profileId: undefined,
      memberType: undefined,
      firstName: '',
      lastName: '',
      email: '',
      emailVerified: false,
      serviceRoles: {},
    };
  }
  return {
    role: readLocalStorage('xservices_role'),
    permissions: readLocalStorageJson<string[]>('xservices_permissions', []),
    userId: Number(readLocalStorage('xservices_user_id')) || undefined,
    memberId: Number(readLocalStorage('xservices_member_id')) || undefined,
    societyId: Number(readLocalStorage('xservices_society_id')) || undefined,
    profileId: Number(readLocalStorage('xservices_profile_id')) || undefined,
    memberType: readLocalStorage('xservices_member_type') || undefined,
    firstName: readLocalStorage('xservices_first_name'),
    lastName: readLocalStorage('xservices_last_name'),
    email: readLocalStorage('xservices_email'),
    emailVerified: readLocalStorage('xservices_email_verified') === 'true',
    serviceRoles: readLocalStorageJson<ServiceRolesMap>('xservices_service_roles', {}),
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authData, setAuthData] = useState<AuthDataState>(getInitialAuthData);

  const setAndPersistAuthData = useCallback((data: any) => {
    if (!data) return;
    const userId = data.userId;
    if (userId) localStorage.setItem('xservices_user_id', String(userId));
    const userUuid = data.userUuid;
    if (userUuid) localStorage.setItem('xservices_user_uuid', userUuid);
    const permissions = data.permissions;
    if (permissions) localStorage.setItem('xservices_permissions', JSON.stringify(permissions));
    const roleTypeCode = data.roleTypeCode;
    if (roleTypeCode) localStorage.setItem('xservices_role', roleTypeCode);
    const societyId = data.societyId;
    if (societyId) {
      localStorage.setItem('xservices_society_id', String(societyId));
    } else {
      localStorage.removeItem('xservices_society_id');
    }
    const profileId = data.profileId;
    if (profileId) {
      localStorage.setItem('xservices_profile_id', String(profileId));
    } else {
      localStorage.removeItem('xservices_profile_id');
    }
    const memberId = data.memberId;
    if (memberId) {
      localStorage.setItem('xservices_member_id', String(memberId));
    } else {
      localStorage.removeItem('xservices_member_id');
    }
    const memberType = data.memberType;
    if (memberType) {
      localStorage.setItem('xservices_member_type', memberType);
    } else {
      localStorage.removeItem('xservices_member_type');
    }
    const firstName = data.firstName;
    if (firstName) localStorage.setItem('xservices_first_name', firstName);
    const lastName = data.lastName;
    if (lastName) localStorage.setItem('xservices_last_name', lastName);
    const email = data.email;
    if (email) localStorage.setItem('xservices_email', email);
    const serviceRoles = data.serviceRoles;
    if (serviceRoles) localStorage.setItem('xservices_service_roles', JSON.stringify(serviceRoles));
    // Persist emailVerified flag
    if (typeof data.emailVerified === 'boolean') {
      localStorage.setItem('xservices_email_verified', String(data.emailVerified));
    }
    
    // Security Standard 27: Store JWT strictly in volatile memory, never in localStorage
    if (data.accessToken) {
      tokenManager.setAccessToken(data.accessToken);
    }
    localStorage.removeItem('xservices_token');
    localStorage.setItem('xservices_authenticated', 'true');

    setAuthData({
      role: roleTypeCode || '',
      permissions: Array.isArray(permissions) ? permissions : [],
      userId: userId ? Number(userId) : undefined,
      memberId: memberId ? Number(memberId) : undefined,
      societyId: societyId ? Number(societyId) : undefined,
      profileId: profileId ? Number(profileId) : undefined,
      memberType: memberType || undefined,
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      emailVerified: typeof data.emailVerified === 'boolean' ? data.emailVerified : false,
      serviceRoles: (serviceRoles && typeof serviceRoles === 'object') ? serviceRoles : {},
    });
  }, []);

  const login = useCallback((data: any) => {
    setAndPersistAuthData(data);
  }, [setAndPersistAuthData]);

  const logout = useCallback(() => {
    localStorage.removeItem('xservices_user_id');
    localStorage.removeItem('xservices_first_name');
    localStorage.removeItem('xservices_last_name');
    localStorage.removeItem('xservices_email');
    localStorage.removeItem('xservices_user_uuid');
    localStorage.removeItem('xservices_permissions');
    localStorage.removeItem('xservices_role');
    localStorage.removeItem('xservices_society_id');
    localStorage.removeItem('xservices_profile_id');
    localStorage.removeItem('xservices_member_id');
    localStorage.removeItem('xservices_member_type');
    localStorage.removeItem('xservices_service_roles');
    localStorage.removeItem('xservices_token');
    localStorage.removeItem('xservices_authenticated');
    localStorage.removeItem('xservices_email_verified');
    tokenManager.clearTokens();

    setAuthData({
      role: '',
      permissions: [],
      userId: undefined,
      memberId: undefined,
      societyId: undefined,
      profileId: undefined,
      memberType: undefined,
      firstName: '',
      lastName: '',
      email: '',
      emailVerified: false,
      serviceRoles: {},
    });
  }, []);

  const isRefreshingRef = useRef(false);

  const refreshSession = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const response = await api.post('/auth/refresh', {}, { skipGlobalErrorHandler: true });
      const data = response.data?.data || response.data;
      if (data) {
        setAndPersistAuthData(data);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Stale or expired session on server: purge state cleanly
        logout();
      } else {
        console.warn('Unable to refresh session:', err?.message || err);
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [setAndPersistAuthData, logout]);

  useEffect(() => {
    // Phase 2: session is tracked by HttpOnly cookie, not localStorage token.
    // Use xservices_authenticated marker (set at login) to decide whether to refresh.
    const isAuthenticated = localStorage.getItem('xservices_authenticated');
    if (isAuthenticated) {
      refreshSession();
    }
  }, [refreshSession]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('xservices_')) {
        setAuthData(getInitialAuthData());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const [currentActiveSocietyId, setCurrentActiveSocietyId] = useState<number | null>(() => getActiveSocietyId());

  useEffect(() => {
    const onSocietyChange = (e: Event) => {
      const ce = e as CustomEvent<{ societyId: number | null }>;
      setCurrentActiveSocietyId(ce.detail?.societyId ?? getActiveSocietyId());
    };
    window.addEventListener(ACTIVE_SOCIETY_CHANGE_EVENT, onSocietyChange);
    return () => window.removeEventListener(ACTIVE_SOCIETY_CHANGE_EVENT, onSocietyChange);
  }, []);

  const markEmailVerified = useCallback(() => {
    localStorage.setItem('xservices_email_verified', 'true');
    setAuthData(prev => ({ ...prev, emailVerified: true }));
  }, []);

  const auth = useMemo((): AuthContextType => {
    const {
      role,
      permissions,
      userId,
      memberId,
      societyId,
      profileId,
      firstName,
      serviceRoles,
    } = authData;

    const availableSocieties: SocietyContextItem[] = Object.entries(serviceRoles)
      .filter(([k]) => k.startsWith('SOCIETY_MGT:'))
      .map(([k, sr]) => {
        const sId = Number(k.split(':')[1]);
        return {
          societyId: sId,
          societyName: `Society #${sId}`,
          roleTypeCode: sr.roleTypeCode,
          roleTypeId: sr.roleTypeId,
          permissions: sr.permissions || [],
        };
      });

    const effectiveSocietyId = currentActiveSocietyId || societyId || (availableSocieties[0]?.societyId ?? null);

    const getServiceRole = (serviceCode: string): ServiceRole | undefined => {
      if (effectiveSocietyId) {
        const key = `${serviceCode}:${effectiveSocietyId}`;
        if (serviceRoles[key]) return serviceRoles[key];
      }
      if (societyId) {
        const key = `${serviceCode}:${societyId}`;
        if (serviceRoles[key]) return serviceRoles[key];
      }
      const foundEntry = Object.entries(serviceRoles).find(([k]) => k.startsWith(`${serviceCode}:`));
      if (foundEntry) return foundEntry[1];
      return serviceRoles[serviceCode];
    };

    const activeServiceRole = getServiceRole('SOCIETY_MGT');
    const effectiveRole = activeServiceRole?.roleTypeCode || role || '';
    const effectivePermissions = activeServiceRole?.permissions?.length
      ? activeServiceRole.permissions
      : permissions;

    const normalizedRole = (effectiveRole || '').replace(/^ROLE_/, '').toUpperCase();
    const serviceRoleCode = (activeServiceRole?.roleTypeCode || '').replace(/^ROLE_/, '').toUpperCase();
    const isGuard = normalizedRole === 'SOCIETY_GUARD' || normalizedRole === 'SOCIETY_GAURD' || normalizedRole === 'GUARD' || serviceRoleCode === 'SOCIETY_GUARD' || activeServiceRole?.roleTypeId === 20;
    const isMember = (normalizedRole === 'MEMBER' || serviceRoleCode === 'MEMBER' || activeServiceRole?.roleTypeId === 12) && !isGuard;
    const canEdit = PRIVILEGED_ROLES.includes(normalizedRole as any) && !isMember && !isGuard;

    const canEditService = (serviceCode: string): boolean => {
      if (isGuard) return false;
      const sr = getServiceRole(serviceCode);
      if (!sr) return canEdit;
      return PRIVILEGED_ROLES.includes(sr.roleTypeCode as typeof PRIVILEGED_ROLES[number]);
    };

    const hasServicePermission = (serviceCode: string, permission: string): boolean => {
      const sr = getServiceRole(serviceCode);
      return sr?.permissions?.includes(permission) ?? false;
    };

    const hasPermission = (permission: string): boolean =>
      effectivePermissions.includes(permission);

    const isAuthenticated = Boolean(userId && localStorage.getItem('xservices_authenticated') === 'true');

    return {
      isAuthenticated,
      role: effectiveRole,
      permissions: effectivePermissions,
      allPermissions: permissions,
      activeSocietyId: effectiveSocietyId,
      availableSocieties,
      switchSociety: (id: number | null) => {
        setActiveSocietyId(id);
        setCurrentActiveSocietyId(id);
      },
      userId,
      memberId,
      societyId: effectiveSocietyId ? Number(effectiveSocietyId) : societyId,
      profileId,
      memberType: authData.memberType,
      firstName,
      lastName: authData.lastName,
      email: authData.email,
      emailVerified: authData.emailVerified,
      canEdit,
      isReadOnly: !canEdit,
      isMember,
      isGuard,
      hasPermission,
      serviceRoles,
      getServiceRole,
      canEditService,
      hasServicePermission,
      refreshSession,
      login,
      logout,
      markEmailVerified,
    };
  }, [authData, currentActiveSocietyId, refreshSession, login, logout, markEmailVerified]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
